import {
  NewReviewFormDialog,
  Report,
  reportParentsCodes,
  ReportService,
  reportTypesChildren,
  RouteReport,
  useCancellablePromise,
} from '@geovelo-frontends/commons';
import { Add } from '@mui/icons-material';
import { Box, Fab, Tooltip } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import { AppContext } from '../../app/context';
import ContributionFormDialog from '../../components/contribution-form-dialog';

import Details from './details';
import ReportsList from './list';

export function Contributions(): React.JSX.Element {
  const {
    map: { current: map },
    user: { current: currentUser },
    zone: { current: currentZone },
    report: { selectedReport, selectedPeriod },
    routing: { contributionId },
    actions: { openSignInDialog, setReports, setSelectedReport },
  } = useContext(AppContext);
  const [reviewDialogOpen, openReviewDialog] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(
    reportParentsCodes
      .filter((key) => reportTypesChildren[key].isBindToOSM)
      .reduce<{ [key: string]: boolean }>((res, key) => {
        res[key] = true;
        return res;
      }, {}),
  );
  const [contributeFormDialogOpen, openContributeFormDialog] = useState(false);
  const { cancellablePromise, cancelPromises } = useCancellablePromise();

  useEffect(() => {
    setReports(undefined);
    if (contributionId === null) getContributions();
  }, [currentZone, selectedTypes, selectedPeriod, contributionId]);

  useEffect(() => {
    if (contributionId) getContribution();
    else setSelectedReport(null);
  }, [contributionId]);

  function selectedTypeChanged(code: string, checked: boolean): void {
    cancelPromises();
    setSelectedTypes({ ...selectedTypes, [code]: checked });
  }

  function handleReviewDialogClose(updatedReport?: Report | RouteReport) {
    if (updatedReport instanceof Report) {
      if (updatedReport) setSelectedReport(updatedReport);
    }

    openReviewDialog(false);
  }

  async function getContribution(): Promise<void> {
    if (!contributionId) return;

    setSelectedReport(undefined);

    try {
      const report = await ReportService.getReport(contributionId);
      setSelectedReport(report);
    } catch (err) {
      console.error(err);
    }
  }

  async function getContributions(): Promise<void> {
    if (!currentZone) return;

    const selectedTypesCodes = reportParentsCodes.filter((key) => selectedTypes[key]);

    const updatedReports =
      selectedTypesCodes.length > 0
        ? (
            await cancellablePromise(
              ReportService.getReports({
                period: selectedPeriod?.toIPeriod(),
                cyclabilityZoneId: currentZone.id,
                typeCodes: selectedTypesCodes,
                page: 1,
                rowsPerPage: 500,
                query:
                  '{id, geo_point, creator{username}, created, description, photos, report_type_code, status}',
                ordering: '-created',
              }),
            )
          ).reports
        : [];
    setReports(updatedReports);
  }

  function handleContributionFormDialogClose(newReport?: Report) {
    if (newReport) getContributions();

    openContributeFormDialog(false);
  }

  return (
    <>
      {contributionId !== null ? (
        <>
          <Details openReviewDialog={openReviewDialog} />
          {selectedReport && (
            <NewReviewFormDialog
              onClose={handleReviewDialogClose}
              open={reviewDialogOpen}
              report={selectedReport}
            />
          )}
        </>
      ) : (
        <>
          <Box display="flex" flexDirection="column" paddingBottom="72px">
            <ReportsList
              selectedTypeChanged={selectedTypeChanged}
              selectedTypes={selectedTypes}
              selectedTypesCount={Object.values(selectedTypes).filter(Boolean).length}
            />
          </Box>
          <Tooltip placement="left" title={<Trans i18nKey="commons.actions.contribute" />}>
            <Fab
              color="secondary"
              onClick={() =>
                currentUser ? openContributeFormDialog(true) : openSignInDialog(true)
              }
              sx={{ bottom: 8, position: 'absolute', right: 8 }}
            >
              <Add color="inherit" />
            </Fab>
          </Tooltip>
        </>
      )}
      <ContributionFormDialog
        initialLocation={map?.getCenter()}
        onClose={handleContributionFormDialogClose}
        open={contributeFormDialogOpen}
      />
    </>
  );
}
